import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { setCustomerEmail } from "@/lib/api";

const STORAGE_KEY = "doria_newsletter_v1";
const DISCOUNT_CODE = "DORIA15";

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return;
    } catch (e) {
      // ignore storage errors
    }

    let dismissed = false;
    const show = () => {
      if (dismissed) return;
      // Don't show on admin pages or if AI advisor is open
      if (window.location.pathname.startsWith("/admin")) return;
      if (document.querySelector('[data-testid="ai-advisor-panel"]')) return;
      if (document.querySelector('[data-testid="quiz-modal"]')) return;
      dismissed = true;
      setOpen(true);
    };

    const timer = setTimeout(show, 15000);

    const onMouseLeave = (e) => {
      if (e.clientY <= 0) show();
    };
    document.addEventListener("mouseleave", onMouseLeave);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  const close = () => {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissedAt: Date.now(), submitted }));
    } catch (e) {
      // ignore
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitted(true);
    setCustomerEmail(trimmed);
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ email: trimmed, submitted: true, at: Date.now() })
      );
    } catch (err) {
      // ignore
    }
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${BACKEND_URL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, source: "popup" }),
      });
    } catch (err) {
      // silent — code still shown to user
    }
    toast.success("You're in. DORIA15 unlocked.");
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(DISCOUNT_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't copy — code is WELCOME10");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 bg-obsidian/50 backdrop-blur-sm z-[60]"
            data-testid="newsletter-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
            className="fixed inset-0 z-[61] flex items-center justify-center p-4 pointer-events-none"
            data-testid="newsletter-popup"
          >
            <div className="pointer-events-auto bg-bone w-full max-w-[520px] relative shadow-2xl">
              <button
                onClick={close}
                data-testid="newsletter-close"
                className="absolute top-4 right-4 p-1 hover:bg-black/5 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-5">
                <div className="sm:col-span-2 bg-[#0A0A0A] text-white p-8 sm:p-6 flex flex-col justify-center">
                  <p className="text-[10px] tracking-[0.3em] uppercase opacity-90 font-sans">
                    15% off your first order
                  </p>
                  <p className="font-serif text-5xl sm:text-6xl leading-none mt-3">
                    15<span className="text-3xl align-top">%</span>
                  </p>
                  <p className="font-serif italic text-lg mt-1">off</p>
                </div>

                <div className="sm:col-span-3 p-8 sm:p-10">
                  {!submitted ? (
                    <>
                      <h3 className="font-serif text-3xl leading-tight">
                        Join the <span className="italic">list</span>.
                      </h3>
                      <p className="text-sm text-neutral-600 mt-3 font-sans leading-relaxed">
                        Early access to drops, editor picks, and{" "}
                        <span className="text-obsidian">15% off your first order</span>.
                      </p>

                      <form onSubmit={handleSubmit} className="mt-6 space-y-3">
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          data-testid="newsletter-popup-email"
                          className="w-full border border-obsidian/20 bg-transparent px-4 py-3 text-sm font-sans focus:outline-none focus:border-obsidian transition-colors"
                        />
                        <button
                          type="submit"
                          data-testid="newsletter-popup-submit"
                          className="w-full bg-obsidian text-bone py-3.5 text-[11px] tracking-[0.3em] uppercase font-sans font-medium hover:bg-neutral-800 transition-colors"
                        >
                          Get my code
                        </button>
                      </form>

                      <button
                        onClick={close}
                        data-testid="newsletter-dismiss"
                        className="mt-4 text-[10px] tracking-widest uppercase text-neutral-500 hover:text-obsidian transition-colors underline underline-offset-4"
                      >
                        No thanks, I'll pay full price
                      </button>
                    </>
                  ) : (
                    <div data-testid="newsletter-success">
                      <div className="w-10 h-10 rounded-full bg-[#0A0A0A]/10 flex items-center justify-center">
                        <Check className="w-5 h-5 text-[#0A0A0A]" />
                      </div>
                      <h3 className="font-serif text-3xl leading-tight mt-4">
                        You're <span className="italic">in</span>.
                      </h3>
                      <p className="text-sm text-neutral-600 mt-3 font-sans leading-relaxed">
                        Use this code at checkout for 15% off your first order.
                      </p>

                      <div className="mt-5 flex items-center gap-3">
                        <div className="flex-1 border border-dashed border-obsidian/30 py-3 px-4 text-center font-serif text-xl tracking-[0.2em]">
                          {DISCOUNT_CODE}
                        </div>
                        <button
                          onClick={copyCode}
                          data-testid="newsletter-copy-code"
                          className="border border-obsidian px-4 py-3 text-[10px] tracking-[0.3em] uppercase font-sans hover:bg-obsidian hover:text-bone transition-colors flex items-center gap-2"
                        >
                          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          {copied ? "Copied" : "Copy"}
                        </button>
                      </div>

                      <button
                        onClick={close}
                        data-testid="newsletter-continue"
                        className="w-full mt-6 bg-obsidian text-bone py-3.5 text-[11px] tracking-[0.3em] uppercase font-sans font-medium hover:bg-neutral-800 transition-colors"
                      >
                        Continue shopping
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
